// ─── src/pages/VisionPage.jsx ─────────────────────────────────────────────────
// "The Vision", LWD's positioning page AND, as of 2026-09-14, the founding
// document for the "Luxury Wedding Intelligence" category: the page formally
// defines the category, states the "world's first Luxury Wedding Intelligence
// Platform" claim, and connects the existing three-layer architecture to the
// concrete systems (Directory, Atlas, The Edit, Magazine, Aura, verification/
// provenance, Publication Integrity, market/venue intelligence, AI visibility
// intelligence) that create it. Deliberately does NOT define the category around
// AI visibility, that is one layer of the system, not the definition of it.
// Aura is framed explicitly as the conversational INTERFACE to the intelligence,
// not the intelligence platform itself.
//
// STILL UNLINKED FROM NAV pending Tai's review of the full page (2026-09-14)
// — do not add nav links or otherwise surface this page until told to.
//
// Design: luxury is CONTRAST, not wall-to-wall black. Sections alternate between
// deep midnight navy and warm ivory/cream so the dark Trust section lands as the
// emotional centrepiece sitting in a sea of light. Palette: midnight navy, warm
// ivory, champagne gold, soft charcoal. ~40% dark / 60% light.
//
// Tone: GROUNDED. Authority from two decades in the industry, real venues, real
// reviews, real editorial, not futurism. Trust is the hero. GEO/AEO framed as the
// road ahead (not a present-tense claim) until prerender ships.
//
// IMPORTANT: SiteFooter is rendered globally from main.jsx, this page must NOT
// render footerNav or its own footer (an object child crashes the page, React #31).

import { useState, useEffect, useRef } from "react";
import { ThemeCtx } from "../theme/ThemeContext";
import { getDarkPalette } from "../theme/tokens";
import { useAura } from "../stores/auraCore";
import HomeNav from "../components/nav/HomeNav";
import { imgUrl } from "../utils/imgUrl";

const GD = "var(--font-heading-primary)";
const NU = "var(--font-body)";
// The single statement image, Burj Al Arab Jumeirah (a real, live LWD venue).
const BURJ = "https://qpkggfibwreznussudfh.supabase.co/storage/v1/object/public/listing-media/vision-burj-d45a1f9a-1c5c-45e3-8973-0e26a2c172cf.jpg";

// ── Tones: every section picks one, and all colours flow from it ────────────────
const TONE = {
  dark:  { bg: "#0f1420", panel: "#171e2d", ink: "#f5f0e7", mute: "rgba(245,240,231,0.68)", faint: "rgba(245,240,231,0.42)", gold: "#cBa978", hair: "rgba(203,169,120,0.55)", line: "rgba(245,240,231,0.12)" },
  ivory: { bg: "#f7f2e9", panel: "#ffffff", ink: "#1a2130", mute: "rgba(26,33,48,0.64)", faint: "rgba(26,33,48,0.42)", gold: "#a4823b", hair: "rgba(164,130,59,0.5)", line: "rgba(26,33,48,0.10)" },
  cream: { bg: "#f0e9da", panel: "#faf6ee", ink: "#1a2130", mute: "rgba(26,33,48,0.64)", faint: "rgba(26,33,48,0.42)", gold: "#a4823b", hair: "rgba(164,130,59,0.5)", line: "rgba(26,33,48,0.10)" },
};

// ── Reveal: fade + rise, fail-safe (never leaves content invisible) ─────────────
function Reveal({ children, delay = 0, style }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    const reveal = () => setShown(true);
    if (!el || typeof IntersectionObserver === "undefined") { reveal(); return undefined; }
    const vh = (typeof window !== "undefined" && window.innerHeight) || 800;
    if (el.getBoundingClientRect().top < vh * 0.92) { reveal(); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { reveal(); io.disconnect(); }
    }, { threshold: 0, rootMargin: "0px 0px -5% 0px" });
    io.observe(el);
    const t = setTimeout(reveal, 2500);
    return () => { io.disconnect(); clearTimeout(t); };
  }, []);
  return (
    <div ref={ref} style={{
      opacity: shown ? 1 : 0,
      transform: shown ? "translateY(0)" : "translateY(26px)",
      transition: `opacity 1s ease ${delay}ms, transform 1.1s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      willChange: "opacity, transform", ...style,
    }}>{children}</div>
  );
}

function Label({ text, t, center }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: center ? "center" : "flex-start", gap: 14, marginBottom: 22 }}>
      <div style={{ width: 28, height: 1, background: t.hair }} />
      <span style={{ fontFamily: NU, fontSize: 10, letterSpacing: "0.30em", textTransform: "uppercase", color: t.gold, fontWeight: 600 }}>{text}</span>
      {center && <div style={{ width: 28, height: 1, background: t.hair }} />}
    </div>
  );
}
const It = (t) => ({ fontStyle: "italic", color: t.gold });

const LAYERS = [
  { tag: "Layer One", title: "Discovery", body: "How couples find. Destinations, venues, planners and ideas, surfaced through the directory, the Atlas, curated collections and editorial. The right venue should never be hidden on page eight.", proof: "Directory · Atlas · Collections · The LWD Edit · Editorial · AI Search Visibility" },
  { tag: "Layer Two", title: "Intelligence", body: "How the platform understands. This is Luxury Wedding Intelligence at work: verified knowledge, editorial authority and market signals, structured so the platform can reason about a couple's style and priorities. Aura is the conversational interface to that intelligence, not the intelligence itself.", proof: "Aura · Wedding Identity · Verified Knowledge · Saved Preferences · Recommendations · Memory" },
  { tag: "Layer Three", title: "Experience", body: "Where couples explore, try, save, compare, enquire and buy. Showcase Studio and Fashion Studio, the boutiques and the Shop, real weddings and real reviews, turning inspiration into confidence, and confidence into commitment.", proof: "Showcase Studio · Fashion Studio · The LWD Edit · Real Weddings · Reviews · Boutiques · The Shop · Appointments" },
];
const MOAT = ["Curation", "Editorial judgment", "Real, attributable reviews", "Real weddings", "Venue relationships", "Human expertise"];

export default function VisionPage({ onBack }) {
  const C = getDarkPalette();
  const setChatContext = useAura((s) => s.setChatContext);
  useEffect(() => { setChatContext?.({ page: "vision" }); }, [setChatContext]);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const go = (p) => { window.history.pushState(null, "", p); window.dispatchEvent(new PopStateEvent("popstate")); };
  const PAD = isMobile ? "92px 22px" : "118px 40px";
  const NARROW = { maxWidth: 760, margin: "0 auto" };
  const WIDE = { maxWidth: 1120, margin: "0 auto" };
  const H1 = (t) => ({ fontFamily: GD, fontWeight: 400, color: t.ink, margin: 0, lineHeight: 1.08, letterSpacing: "-0.01em" });
  const H2 = (t) => ({ fontFamily: GD, fontWeight: 400, color: t.ink, margin: 0, lineHeight: 1.16, fontSize: "clamp(30px, 3.4vw, 50px)" });
  const LEAD = (t) => ({ fontFamily: NU, fontWeight: 300, color: t.mute, fontSize: "clamp(16px, 1.4vw, 20px)", lineHeight: 1.8 });
  const Sec = ({ t, pad, children, style }) => (
    <section style={{ background: t.bg, padding: pad || PAD, borderTop: `1px solid ${t.line}`, ...style }}>{children}</section>
  );
  const btn = (t, primary) => ({
    fontFamily: NU, fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
    padding: "15px 30px", borderRadius: 2, cursor: "pointer",
    background: primary ? t.gold : "transparent",
    color: primary ? (t.bg === TONE.dark.bg ? "#171008" : "#fff") : t.ink,
    border: primary ? "none" : `1px solid ${t.hair}`, transition: "opacity 0.2s",
  });

  const D = TONE.dark, IV = TONE.ivory, CR = TONE.cream;

  return (
    <ThemeCtx.Provider value={C}>
      <div style={{ background: D.bg }}>
        <HomeNav darkMode onToggleDark={() => {}} onVendorLogin={onBack}
          onNavigateAbout={() => go("/about-us")} onNavigateStandard={() => go("/the-lwd-standard")} hasHero />

        <main>
          {/* 1 ── HERO (dark) */}
          <section style={{ background: D.bg, padding: isMobile ? "148px 22px 100px" : "208px 40px 128px", textAlign: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: "-32%", left: "50%", transform: "translateX(-50%)", width: 940, height: 940, borderRadius: "50%", background: "radial-gradient(circle, rgba(203,169,120,0.12), transparent 62%)", pointerEvents: "none" }} />
            <Reveal style={{ ...NARROW, position: "relative" }}>
              <Label text="The Vision" t={D} center />
              <h1 style={{ ...H1(D), fontSize: "clamp(36px, 5vw, 70px)" }}>
                For twenty years, we helped couples <span style={It(D)}>search.</span><br />
                Now we are helping them <span style={It(D)}>choose.</span>
              </h1>
              <p style={{ ...LEAD(D), margin: "38px auto 0", maxWidth: 600 }}>
                We have spent two decades inside the luxury wedding industry, watching how couples find venues and how venues find couples. That relationship is changing, and we have been here long enough to see where it is going.
              </p>
              <div style={{ marginTop: 44, display: "flex", flexWrap: "wrap", gap: 14, justifyContent: "center" }}>
                <button onClick={onBack} style={btn(D, true)}>Explore the Directory</button>
                <button onClick={() => go("/apply-for-inclusion")} style={btn(D, false)}>Apply for Inclusion</button>
              </div>
            </Reveal>
          </section>

          {/* 2 ── THE CATEGORY (ivory): formal definition of Luxury Wedding Intelligence */}
          <Sec t={IV}>
            <Reveal style={NARROW}>
              <Label text="The Category" t={IV} />
              <h2 style={{ ...H2(IV), marginBottom: 28 }}>
                Luxury Wedding Directory is <span style={It(IV)}>the world's first Luxury Wedding Intelligence Platform.</span>
              </h2>
              <p style={{ ...LEAD(IV), marginBottom: 20 }}>
                Luxury Wedding Intelligence is the collection, verification, structuring and interpretation of luxury wedding knowledge, destination expertise, venue and creative data, editorial content, market signals and couple intent, combined to power intelligent discovery, recommendation and decision-making across the entire luxury wedding journey.
              </p>
              <p style={LEAD(IV)}>
                This is not a directory with a chatbot attached, and it is not a tool that simply monitors how a venue appears in an AI answer. Those are single outputs of a much larger system. Luxury Wedding Intelligence is the system itself, an entire industry's knowledge, structured over two decades, so it can be discovered, understood and trusted by couples and by the AI systems now standing between them.
              </p>
            </Reveal>
          </Sec>

          {/* 3 ── THE PROBLEM (ivory) */}
          <Sec t={IV}>
            <Reveal style={NARROW}>
              <Label text="The Problem" t={IV} />
              <h2 style={{ ...H2(IV), marginBottom: 28 }}>More information.<br /><span style={It(IV)}>Less guidance.</span></h2>
              <p style={{ ...LEAD(IV), marginBottom: 20 }}>For years, couples have been asked to do all the work. Search hundreds of venues. Compare countless websites. Scroll endlessly. Save screenshots, build spreadsheets, and start again.</p>
              <p style={LEAD(IV)}>The industry has grown richer in information and poorer in guidance. More options have not made better decisions, and every directory makes every venue look the same.</p>
            </Reveal>
          </Sec>

          {/* 4 ── THREE LAYERS (dark, with light cards) */}
          <Sec t={D}>
            <Reveal style={{ ...WIDE, textAlign: "center", marginBottom: isMobile ? 44 : 62 }}>
              <Label text="The Architecture" t={D} center />
              <h2 style={H2(D)}>Three layers.<br /><span style={It(D)}>One continuous journey.</span></h2>
            </Reveal>
            <div style={{ ...WIDE, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? 18 : 26 }}>
              {LAYERS.map((l, i) => (
                <Reveal key={l.title} delay={i * 90}>
                  <div style={{ background: D.panel, border: `1px solid ${D.line}`, borderRadius: 4, padding: isMobile ? "30px 26px" : "40px 34px", height: "100%" }}>
                    <p style={{ fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: D.gold, margin: "0 0 18px" }}>{l.tag}</p>
                    <h3 style={{ fontFamily: GD, fontWeight: 400, fontSize: 30, color: D.ink, margin: "0 0 16px" }}>{l.title}</h3>
                    <p style={{ fontFamily: NU, fontWeight: 300, fontSize: 15, lineHeight: 1.75, color: D.mute, margin: 0 }}>{l.body}</p>
                    {l.proof && (
                      <p style={{ fontFamily: NU, fontWeight: 600, fontSize: 11, letterSpacing: "0.05em", lineHeight: 1.85, color: D.gold, margin: "20px 0 0", paddingTop: 18, borderTop: `1px solid ${D.line}`, opacity: 0.92 }}>{l.proof}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </Sec>

          {/* 5 ── HOW THE INTELLIGENCE IS CREATED (dark): the 9-part system behind
               the category definition above. Order is deliberate, AI Visibility
               Intelligence sits LAST, one layer among nine, not the headline. */}
          <Sec t={D}>
            <Reveal style={{ ...WIDE, textAlign: "center", marginBottom: isMobile ? 40 : 54 }}>
              <Label text="The System" t={D} center />
              <h2 style={H2(D)}>How Luxury Wedding Intelligence<br /><span style={It(D)}>is created.</span></h2>
              <p style={{ ...LEAD(D), maxWidth: 700, margin: "22px auto 0" }}>
                Nine parts of one system, and each builds on the one before it. Listings structure the facts. The Atlas structures the destination. The Edit reads that structure and interprets it editorially. The Magazine gives it visual and inspirational form. Aura interrogates all of it in conversation. Verification protects the whole chain.
              </p>
            </Reveal>
            <Reveal>
              <div style={{ ...WIDE, display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: 1, background: D.line, border: `1px solid ${D.line}` }}>
                {[
                  ["Directory Listings", "Every venue and creative transformed into structured, verified knowledge, from capacity and ceremony options to accommodation, pricing signals, facilities and imagery. More than a marketing page, it becomes part of the intelligence."],
                  ["Wedding Atlas", "Destinations and regions organised into a global map of where, and when, to marry well."],
                  ["The Edit", "The same structured knowledge, read by an editor's eye. Editorial interpretation and authority, the context that turns a fact into a reason to choose."],
                  ["Luxury Wedding Magazine", "Verified knowledge given visual and inspirational form. Wedding storytelling built from the same trusted facts, not separate from them."],
                  ["Aura", "The conversational interface to all of it. Couples describe intent, guest numbers, destination and style, and Aura reasons across the intelligence to respond, it is how the intelligence is reached, not the intelligence itself."],
                  ["Verified Knowledge & Provenance", "Every fact checked against its source, contradictions caught, nothing published on assumption."],
                  ["Publication Integrity", "Ongoing verification of what is live, accurate and properly attributed across the platform."],
                  ["Market & Venue Intelligence", "Turning the system around, giving venues and businesses intelligence about their own positioning, competitors and opportunities."],
                  ["AI Visibility Intelligence", "How venues and brands are discovered and cited by the AI systems now standing between a couple and their decision, one layer of the intelligence, not its definition."],
                ].map(([h, b]) => {
                  // Aura gets a restrained warm gold-tinted panel, deliberately (final
                  // call, 2026-09-15, after trying both ways): it reads as the ACTIVE
                  // INTERFACE glowing within the system, not a separate/dominant
                  // product, because it stays in the same dark family as the other
                  // eight cards, just lit differently. This is exactly right with the
                  // "how the intelligence is reached, not the intelligence itself" line
                  // below it. Do NOT brighten it further or add an icon/badge, that
                  // would tip it into looking like the product with everything else as
                  // supporting infrastructure, the thing this page is arguing against.
                  const isAura = h === "Aura";
                  return (
                    <div key={h} style={{ background: isAura ? "linear-gradient(160deg, rgba(203,169,120,0.16), rgba(203,169,120,0.05))" : D.panel, padding: isMobile ? "26px 20px" : "34px 30px", ...(isAura ? { boxShadow: `inset 0 0 0 1px ${D.hair}` } : {}) }}>
                      <p style={{ fontFamily: GD, fontSize: isMobile ? 18 : 21, color: isAura ? D.gold : D.ink, margin: "0 0 12px" }}>{h}</p>
                      <p style={{ fontFamily: NU, fontWeight: 300, fontSize: 14, lineHeight: 1.7, color: isAura ? "rgba(245,240,231,0.82)" : D.mute, margin: 0 }}>{b}</p>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </Sec>

          {/* 6 ── WEDDING IDENTITY (ivory) */}
          <Sec t={IV}>
            <Reveal style={NARROW}>
              <Label text="Discovery Should Feel Personal" t={IV} />
              <h2 style={{ ...H2(IV), marginBottom: 28 }}>The Wedding Identity</h2>
              <p style={{ ...LEAD(IV), marginBottom: 20 }}>A wedding on Lake Como is not a wedding in Marrakech. A bride searching for couture is not a couple planning an intimate gathering in the English countryside. Traditional directories treat every visitor the same. We do not.</p>
              <p style={LEAD(IV)}>The Wedding Identity is a discovery experience that learns. As a couple explores, saves and talks to Aura, the platform comes to understand their style and priorities, and the guidance grows more relevant with every visit. Discovery begins with understanding.</p>
            </Reveal>
          </Sec>

          {/* 7 ── AURA (dark, was cream — moved 2026-09-15 so it stands apart from the
               Wedding Identity/Atlas light sections either side of it, and pairs
               thematically with the other architecture/intelligence sections) */}
          <Sec t={D}>
            <Reveal style={NARROW}>
              <Label text="Guidance Over Search" t={D} />
              <h2 style={{ ...H2(D), marginBottom: 28 }}>Aura, the voice of<br /><span style={It(D)}>Luxury Wedding Intelligence.</span></h2>
              <p style={{ ...LEAD(D), marginBottom: 20 }}>Aura is not a search box with a new coat of paint, and it is not the intelligence itself. It is the conversational interface to it, the way a couple reaches everything the platform knows, built to guide them through the journey, helping them discover destinations, understand styles, weigh options and find trusted creatives with confidence.</p>
              <p style={LEAD(D)}>Rather than searching through thousands of options, couples are guided toward the few most aligned with their vision. The future of luxury discovery is not more information. It is better guidance.</p>
            </Reveal>
          </Sec>

          {/* 8 ── THE ATLAS (ivory, expanded) */}
          <Sec t={IV}>
            <div style={WIDE}>
              <Reveal style={{ maxWidth: 760, marginBottom: isMobile ? 36 : 50 }}>
                <Label text="Context, Not Content" t={IV} />
                <h2 style={{ ...H2(IV), marginBottom: 28 }}>The Atlas, a map of<br /><span style={It(IV)}>where to marry well.</span></h2>
                <p style={{ ...LEAD(IV), marginBottom: 20 }}>Knowledge creates confidence. The Atlas is a growing body of destination intelligence, regional expertise and planning insight, written for couples planning extraordinary celebrations. It is the layer that turns a search into a sense of place.</p>
                <p style={LEAD(IV)}>As discovery moves from search boxes to AI assistants, the Atlas is being built so the right region, season and venue can be understood and recommended wherever couples ask. It is not simply content. It is context.</p>
              </Reveal>
              <Reveal>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 1, background: IV.line, border: `1px solid ${IV.line}` }}>
                  {[["Destinations", "Italy, France, and the world's most sought-after settings"], ["Regional Expertise", "Where to marry, and when, region by region"], ["Planning Intelligence", "The practical knowledge behind a confident decision"], ["Discovery Context", "Structured so AI assistants can cite it, not just couples"]].map(([h, b]) => (
                    <div key={h} style={{ background: IV.panel, padding: isMobile ? "24px 18px" : "30px 26px" }}>
                      <p style={{ fontFamily: GD, fontSize: isMobile ? 17 : 20, color: IV.ink, margin: "0 0 10px" }}>{h}</p>
                      <p style={{ fontFamily: NU, fontWeight: 300, fontSize: 13.5, lineHeight: 1.6, color: IV.mute, margin: 0 }}>{b}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </Sec>

          {/* 9 ── STATEMENT BREAK (cream, big type, no body) */}
          <Sec t={CR} pad={isMobile ? "104px 22px" : "168px 40px"}>
            <Reveal style={{ maxWidth: 940, margin: "0 auto", textAlign: "center" }}>
              <h2 style={{ ...H1(CR), fontSize: "clamp(32px, 5.2vw, 72px)" }}>
                Couples do not need more venues.<br /><span style={It(CR)}>They need confidence.</span>
              </h2>
            </Reveal>
          </Sec>

          {/* ── Statement image (one full-bleed editorial moment) ── */}
          <div style={{ position: "relative", width: "100%", height: isMobile ? "58vh" : "clamp(420px, 66vh, 700px)", overflow: "hidden", background: D.bg }}>
            <img src={imgUrl(BURJ, { width: 1800 })} alt="Burj Al Arab Jumeirah, Dubai" loading="lazy"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(15,20,32,0.10) 0%, transparent 34%, transparent 62%, rgba(12,17,27,0.55) 100%)" }} />
            <a href="/uae/dubai/wedding-venues/burj-al-arab-jumeirah"
              onClick={(e) => { e.preventDefault(); go("/uae/dubai/wedding-venues/burj-al-arab-jumeirah"); }}
              style={{ position: "absolute", left: isMobile ? 22 : 48, bottom: isMobile ? 22 : 40, fontFamily: NU, fontSize: 10, fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(245,240,231,0.92)", textDecoration: "none" }}>
              Burj Al Arab Jumeirah &middot; Dubai
            </a>
          </div>

          {/* 10 ── TRUST (dark, the centrepiece) */}
          <section style={{ background: `linear-gradient(180deg, ${D.bg} 0%, #0c111b 100%)`, padding: isMobile ? "104px 22px" : "156px 40px", borderTop: `1px solid ${D.hair}` }}>
            <Reveal style={{ ...WIDE, textAlign: "center", marginBottom: isMobile ? 40 : 54 }}>
              <Label text="Why It Lasts" t={D} center />
              <h2 style={{ ...H1(D), fontSize: "clamp(32px, 4.2vw, 60px)" }}>
                In an AI world,<br /><span style={It(D)}>trust is the moat.</span>
              </h2>
              <p style={{ fontFamily: NU, fontSize: 11, fontWeight: 600, letterSpacing: "0.24em", textTransform: "uppercase", color: D.gold, margin: "24px 0 0" }}>
                Trusted by luxury wedding venues since 2006
              </p>
            </Reveal>
            <Reveal style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
              <p style={{ ...LEAD(D), marginBottom: 22 }}>Anyone can buy artificial intelligence. What cannot be bought is twenty years inside this industry, a network of real venues, real reviews from real couples, editorial judgment, and the human eye that decides what belongs.</p>
              <p style={{ ...LEAD(D), marginBottom: 22 }}>Every venue is reviewed. Every review is real and attributable. Every recommendation is grounded in relevance, never invented to fill a page. Aura is powerful precisely because it stands on this foundation. The intelligence is only ever as good as the curation beneath it.</p>
              <p style={{ ...LEAD(D), color: D.ink }}>As AI-generated content floods the industry, that foundation becomes the rarest thing of all. Technology should strengthen trust, not replace it.</p>
            </Reveal>
            <Reveal style={{ ...WIDE, marginTop: isMobile ? 44 : 62 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: 1, background: D.line, border: `1px solid ${D.line}` }}>
                {MOAT.map((m) => (
                  <div key={m} style={{ background: D.bg, padding: isMobile ? "26px 14px" : "34px 24px", textAlign: "center" }}>
                    <span style={{ fontFamily: GD, fontSize: isMobile ? 16 : 21, color: D.ink }}>{m}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </section>

          {/* 11 ── THE FUTURE (ivory) */}
          <Sec t={IV}>
            <Reveal style={NARROW}>
              <Label text="Looking Ahead" t={IV} />
              <h2 style={{ ...H2(IV), marginBottom: 28 }}>Built for where the<br /><span style={It(IV)}>industry is going.</span></h2>
              <p style={{ ...LEAD(IV), marginBottom: 20 }}>Search engines are becoming assistants. Recommendations are becoming personal. Directories are becoming intelligent platforms. We have been preparing for that shift for twenty years.</p>
              <p style={LEAD(IV)}>The luxury wedding industry deserves more than another directory. It deserves a platform built for the next decade, one that combines discovery, intelligence, trust and experience into a single, considered whole.</p>
            </Reveal>
          </Sec>

          {/* 12 ── OUR VISION + CTA (cream) */}
          <Sec t={CR} pad={isMobile ? "100px 22px" : "138px 40px"} style={{ textAlign: "center" }}>
            <Reveal style={NARROW}>
              <Label text="Our Vision" t={CR} center />
              <h2 style={{ ...H1(CR), fontSize: "clamp(28px, 3.4vw, 46px)", marginBottom: 30 }}>
                To become the world's most trusted destination for <span style={It(CR)}>luxury wedding discovery.</span>
              </h2>
              <p style={{ ...LEAD(CR), margin: "0 auto 22px", maxWidth: 600 }}>
                Whether you are a couple planning a celebration, a venue shaping unforgettable days, or a creative defining your craft, we invite you to be part of what comes next.
              </p>
              <p style={{ fontFamily: GD, fontStyle: "italic", fontSize: "clamp(20px, 2.2vw, 30px)", color: CR.ink, lineHeight: 1.4, margin: "0 auto 44px", maxWidth: 760 }}>
                The next decade of luxury weddings will belong to those who earn trust, not attention.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 14, justifyContent: "center" }}>
                <button onClick={onBack} style={btn(CR, true)}>Explore the Directory</button>
                <button onClick={() => go("/apply-for-inclusion")} style={btn(CR, false)}>Apply for Inclusion</button>
              </div>
            </Reveal>
          </Sec>
        </main>
        {/* SiteFooter is rendered globally from main.jsx, do not render it here. */}
      </div>
    </ThemeCtx.Provider>
  );
}
